/**
 * businessSetup preset — voice input for the SalesGod Intelligence Setup
 * (UltimateSetupModal). Turns a spoken business intro into the fields the
 * setup pipeline needs.
 *
 * Schema type: 'fields'
 *   The AI extracts named typed values that map 1:1 onto UltimateSetup's
 *   form fields:
 *     - rawInfo      (long_string, required) — the polished business dump
 *                    used by the Ultimate Setup pipeline as its primary input
 *     - companyName  (string, optional)
 *     - name         (string, optional)     — the speaker's name
 *     - role         (string, optional)
 *     - language     (enum, optional)       — ISO-639-1 code detected from the transcript
 *     - targetSize   (enum, optional)       — company-size bucket for target audience
 *
 * `rawInfo` is deliberately the biggest field because the downstream
 * Ultimate Setup pipeline is trained to extract everything else from a
 * well-written business narrative; the extra fields are just hints that
 * pre-fill the tiny secondary inputs above the textarea.
 *
 * Preview: a compact editable form so the user can tweak anything before
 * confirming.
 */

import React, { useEffect, useState } from 'react';

const LANGUAGE_OPTIONS = [
  { value: '',   label: 'Auto-detect' },
  { value: 'de', label: 'German' },
  { value: 'en', label: 'English' },
  { value: 'fr', label: 'French' },
  { value: 'es', label: 'Spanish' },
  { value: 'it', label: 'Italian' },
  { value: 'nl', label: 'Dutch' },
];

const TARGET_SIZE_OPTIONS = [
  { value: '',           label: 'Auto-detect from description' },
  { value: 'solo',       label: 'Solo / freelancer (1 person)' },
  { value: 'micro',      label: 'Micro (2–10 employees)' },
  { value: 'small',      label: 'Small (11–50 employees)' },
  { value: 'mid',        label: 'Mid-size (51–250 employees)' },
  { value: 'large',      label: 'Large (251–1000 employees)' },
  { value: 'enterprise', label: 'Enterprise (1000+ employees)' },
];

// Field definitions consumed by BOTH the backend prompt-builder AND the
// preview form. Keeping them in one place keeps the AI contract and the
// UI schema in sync.
export const BUSINESS_SETUP_FIELDS = [
  {
    id: 'rawInfo',
    label: 'Business dump',
    type: 'long_string',
    required: true,
    hint: 'Everything the model needs to design the outreach setup — business, audience, offer, USPs, proof, tone. Write in polished prose the user could ship as-is; do not use bullet lists or headings.',
  },
  {
    id: 'companyName',
    label: 'Company name',
    type: 'string',
    hint: 'The company or brand the user mentions. Leave empty if not clearly stated.',
  },
  {
    id: 'name',
    label: 'Speaker name',
    type: 'string',
    hint: 'The person speaking, if they introduce themselves. Otherwise empty.',
  },
  {
    id: 'role',
    label: 'Role / title',
    type: 'string',
    hint: 'The speaker\'s role (Founder, CEO, Head of Sales, …) if mentioned.',
  },
  {
    id: 'language',
    label: 'Language',
    type: 'enum',
    options: LANGUAGE_OPTIONS.map((o) => o.value).filter(Boolean),
    hint: 'ISO-639-1 code of the language the user actually spoke in. Empty means auto-detect.',
  },
  {
    id: 'targetSize',
    label: 'Target company size',
    type: 'enum',
    options: TARGET_SIZE_OPTIONS.map((o) => o.value).filter(Boolean),
    hint: 'Size bucket of the audience the user is targeting. Only fill if the user gave a clear hint (SMB / mid-market / enterprise / individuals). Empty means auto-detect.',
  },
];

/**
 * Editable preview form. Values arrive prefilled from the AI, the user can
 * tweak anything, then confirms.
 */
function FieldsPreview({ structured, onConfirm, onCancel, saving }) {
  const initial = structured?.fields || {};
  const [values, setValues] = useState({
    rawInfo: initial.rawInfo || '',
    companyName: initial.companyName || '',
    name: initial.name || '',
    role: initial.role || '',
    language: initial.language || '',
    targetSize: initial.targetSize || '',
  });

  // If the modal is re-opened with fresh AI output while mounted, re-seed.
  useEffect(() => {
    if (initial) {
      setValues({
        rawInfo: initial.rawInfo || '',
        companyName: initial.companyName || '',
        name: initial.name || '',
        role: initial.role || '',
        language: initial.language || '',
        targetSize: initial.targetSize || '',
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structured?.fields?.rawInfo]);

  const update = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));

  const canConfirm = values.rawInfo.trim().length >= 120;
  const rawInfoLen = values.rawInfo.length;

  return (
    <div className="speakEngine__body speakEngine__body--preview">
      <div className="speakEngine__previewHead">
        <div>
          <h3>Ready to launch setup</h3>
          {structured?.summary && <p className="speakEngine__previewSummary">{structured.summary}</p>}
        </div>
      </div>

      <div className="speakEngine__fieldsGrid">
        <label className="speakEngine__field speakEngine__field--wide">
          <span className="speakEngine__fieldLabel">
            Business dump
            <span className={`speakEngine__fieldCounter ${rawInfoLen < 150 ? 'is-low' : ''}`}>
              {rawInfoLen} chars {rawInfoLen < 150 && '· at least 150 recommended'}
            </span>
          </span>
          <textarea
            className="speakEngine__fieldTextarea"
            value={values.rawInfo}
            onChange={(e) => update('rawInfo', e.target.value)}
            rows={9}
            placeholder="The polished business narrative the Ultimate Setup will use as its primary input."
          />
        </label>

        <div className="speakEngine__fieldsRow">
          <label className="speakEngine__field">
            <span className="speakEngine__fieldLabel">Company name</span>
            <input
              type="text"
              className="speakEngine__fieldInput"
              value={values.companyName}
              onChange={(e) => update('companyName', e.target.value)}
              placeholder="Auto-detected from your dump"
            />
          </label>
          <label className="speakEngine__field">
            <span className="speakEngine__fieldLabel">Speaker name</span>
            <input
              type="text"
              className="speakEngine__fieldInput"
              value={values.name}
              onChange={(e) => update('name', e.target.value)}
              placeholder="Optional"
            />
          </label>
        </div>

        <div className="speakEngine__fieldsRow">
          <label className="speakEngine__field">
            <span className="speakEngine__fieldLabel">Role / title</span>
            <input
              type="text"
              className="speakEngine__fieldInput"
              value={values.role}
              onChange={(e) => update('role', e.target.value)}
              placeholder="Optional"
            />
          </label>
          <label className="speakEngine__field">
            <span className="speakEngine__fieldLabel">Language</span>
            <select
              className="speakEngine__fieldInput"
              value={values.language}
              onChange={(e) => update('language', e.target.value)}
            >
              {LANGUAGE_OPTIONS.map((o) => (
                <option key={o.value || 'auto'} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
        </div>

        <label className="speakEngine__field">
          <span className="speakEngine__fieldLabel">Target company size</span>
          <select
            className="speakEngine__fieldInput"
            value={values.targetSize}
            onChange={(e) => update('targetSize', e.target.value)}
          >
            {TARGET_SIZE_OPTIONS.map((o) => (
              <option key={o.value || 'auto'} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="speakEngine__previewActions">
        <button type="button" className="speakEngine__btn speakEngine__btn--ghost" onClick={onCancel} disabled={saving}>
          Back to transcript
        </button>
        <button
          type="button"
          className="speakEngine__btn speakEngine__btn--primary"
          onClick={() => onConfirm(values)}
          disabled={saving || !canConfirm}
          title={!canConfirm ? 'The business dump is too short to run a good setup. Aim for at least 150 characters.' : ''}
        >
          {saving
            ? <><span className="speakEngine__btnSpinner" aria-hidden /> Applying…</>
            : <>Use these values</>}
        </button>
      </div>
    </div>
  );
}

const businessSetupPreset = {
  id: 'businessSetup',
  title: 'Tell us about your business',
  subtitle: 'AI writes it up and pre-fills the setup for you.',
  intro: {
    heading: 'Tap and start talking',
    hint: 'Cover what you do, who you sell to, your offer, the main problems you solve, and any proof or numbers. 90 seconds is enough. 3 minutes is plenty.',
    chips: ['Auto-writes the business dump', 'Fills company + language', 'Never lose a word'],
  },
  organizeCta: 'Write it up with AI',
  organizingText: 'AI is polishing your words into a clean business narrative the Ultimate Setup can act on. Usually 3–8 seconds.',
  doneText: () => 'Setup prefilled. Review and launch.',
  schema: {
    type: 'fields',
    fields: BUSINESS_SETUP_FIELDS,
  },
  PreviewComponent: FieldsPreview,
};

export default businessSetupPreset;
